import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren
} from 'react';
import {
  domainMentionLabel,
  type DomainMention
} from '../utils/domainMentionMarkers';

type McpComposerMentionsContextValue = {
  /** Text that stays immediately before the domain badge(s). */
  textBeforeMentions: string;
  domainMentions: DomainMention[];
  setTextBeforeMentions: (value: string) => void;
  /**
   * Place a domain badge after `textBeforeAt`.
   * On the first badge, `textBeforeAt` becomes the leading text.
   * On later badges, `textBeforeAt` is appended to the leading text.
   */
  insertDomainMention: (
    domain: DomainMention,
    textBeforeAt: string
  ) => void;
  /** Removes badge; returns leading text to restore when it was the last badge. */
  removeDomainMention: (slug: string) => string | null;
  clearDomainMentions: () => void;
  /**
   * Collapse leading text + badges + trailing composer text into one plain string
   * and clear mention state (so the whole message is editable/selectable again).
   */
  flattenToPlainText: (trailingComposerText: string) => string;
};

const McpComposerMentionsContext =
  createContext<McpComposerMentionsContextValue | null>(null);

export const McpComposerMentionsProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [textBeforeMentions, setTextBeforeMentions] = useState('');
  const [domainMentions, setDomainMentions] = useState<DomainMention[]>([]);

  const insertDomainMention = useCallback(
    (domain: DomainMention, textBeforeAt: string) => {
      const chunk = textBeforeAt.replace(/\u200b/g, '');
      setDomainMentions(prev => {
        const isFirst = prev.length === 0;
        setTextBeforeMentions(before => (isFirst ? chunk : `${before}${chunk}`));
        if (prev.some(d => d.slug === domain.slug)) return prev;
        return [...prev, domain];
      });
    },
    []
  );

  const removeDomainMention = useCallback(
    (slug: string): string | null => {
      const next = domainMentions.filter(d => d.slug !== slug);
      const restore =
        next.length === 0 && domainMentions.length > 0
          ? textBeforeMentions
          : null;
      setDomainMentions(next);
      if (restore !== null) {
        setTextBeforeMentions('');
      }
      return restore;
    },
    [domainMentions, textBeforeMentions]
  );

  const clearDomainMentions = useCallback(() => {
    setDomainMentions([]);
    setTextBeforeMentions('');
  }, []);

  const flattenToPlainText = useCallback(
    (trailingComposerText: string) => {
      const trailing = trailingComposerText.replace(/\u200b/g, '');
      const badgeText = domainMentions
        .map(d => domainMentionLabel(d))
        .join(' ');
      const merged = `${textBeforeMentions}${badgeText}${
        badgeText && trailing && !trailing.startsWith(' ') ? ' ' : ''
      }${trailing}`;
      setDomainMentions([]);
      setTextBeforeMentions('');
      return merged;
    },
    [domainMentions, textBeforeMentions]
  );

  const value = useMemo<McpComposerMentionsContextValue>(
    () => ({
      textBeforeMentions,
      domainMentions,
      setTextBeforeMentions,
      insertDomainMention,
      removeDomainMention,
      clearDomainMentions,
      flattenToPlainText
    }),
    [
      textBeforeMentions,
      domainMentions,
      insertDomainMention,
      removeDomainMention,
      clearDomainMentions,
      flattenToPlainText
    ]
  );

  return (
    <McpComposerMentionsContext.Provider value={value}>
      {children}
    </McpComposerMentionsContext.Provider>
  );
};

export function useMcpComposerMentions(): McpComposerMentionsContextValue {
  const ctx = useContext(McpComposerMentionsContext);
  if (!ctx) {
    throw new Error(
      'useMcpComposerMentions must be used within McpComposerMentionsProvider'
    );
  }
  return ctx;
}

export function useOptionalMcpComposerMentions(): McpComposerMentionsContextValue | null {
  return useContext(McpComposerMentionsContext);
}
